import { useEffect, useState } from "react";
import { X, ZoomIn, ExternalLink } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const SOURCES = [
  {
    label: "American Academy of Pediatrics — 2022 Safe-Sleep Recommendations",
    href: "https://publications.aap.org/pediatrics/article/150/1/e2022057990/188304/Sleep-Related-Infant-Deaths-Updated-2022-Recommendations",
  },
  {
    label: "The Lullaby Trust — Guidance on Dummies and Safer Sleep",
    href: "https://www.lullabytrust.org.uk/baby-safety/baby-product-information/dummies/",
  },
];

const ALT_TEXT =
  "Mi Binky information flyer explaining independent pacifier and safer-sleep research, why Mi Binky was created and its current conformity-testing status.";

export const SaferSleep = () => {
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <section id="safer-sleep" data-testid="safer-sleep-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute -right-40 top-24 h-[420px] w-[420px] rounded-full bg-binky-pink/10 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Eyebrow className="justify-center text-binky-pink">Pacifiers and Safer Sleep</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Pacifiers and <span className="text-binky-blue">Safer Sleep</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg" data-testid="safer-sleep-intro">
            Independent research has found an association between pacifier use during sleep and a
            lower risk of SIDS. This flyer explains what the research says, why we created Mi Binky
            and the important limits of any claims.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14 flex justify-center">
          <button
            data-testid="safer-sleep-flyer-button"
            onClick={() => setLightbox(true)}
            className="group relative block w-full max-w-3xl cursor-zoom-in overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_24px_60px_-24px_rgba(27,79,160,0.3)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_36px_72px_-24px_rgba(240,62,155,0.35)]"
            aria-label="View the Pacifiers and Safer Sleep flyer full-size"
          >
            <img
              src="/assets/safer-sleep-flyer.png"
              alt={ALT_TEXT}
              loading="lazy"
              data-testid="safer-sleep-flyer-image"
              className="h-auto w-full object-contain"
            />
            <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-binky-navy/85 px-4 py-2 text-xs font-bold text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
              <ZoomIn className="h-3.5 w-3.5" />
              Tap to view full-size
            </span>
          </button>
        </Reveal>

        <Reveal delay={0.15} className="mt-8">
          <div className="flex flex-col items-center gap-2 text-center" data-testid="safer-sleep-sources">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Sources</span>
            {SOURCES.map((s) => (
              <a
                key={s.href}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`source-link-${s.label.includes("Lullaby") ? "lullaby-trust" : "aap"}`}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 underline decoration-binky-pink/30 underline-offset-4 transition-colors hover:text-binky-blue"
              >
                {s.label}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
            ))}
          </div>
        </Reveal>
      </div>

      {lightbox && (
        <div
          data-testid="safer-sleep-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-binky-navy/90 p-4 backdrop-blur-md sm:p-8"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Pacifiers and Safer Sleep flyer — full-size view"
        >
          <button
            data-testid="safer-sleep-lightbox-close"
            onClick={() => setLightbox(false)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-binky-pink"
            aria-label="Close full-size flyer"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src="/assets/safer-sleep-flyer.png"
            alt={ALT_TEXT}
            data-testid="safer-sleep-lightbox-image"
            className="max-h-[92vh] max-w-[94vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};
